import { Tenant } from '../../../application/types/tenants.ts';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import TenantService from './services/tenantService.ts';
import { useGlobalStore } from '../../../application/store/store.tsx';
import Loader from '../../../common/components/Loader.tsx';
import {
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { DateTime } from 'luxon';
import { NavigationRoutes } from '../../../application/router/routes.ts';
import { AllowedTransactionRegistrations } from '../../../application/types/allowedTransactionRegistrations.ts';

const TenantView = () => {
    const tenantService = new TenantService();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const { setMessage } = useGlobalStore((state) => state);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTenant = async () => {
            try {
                setIsLoading(true);
                const response = await tenantService.getTenant(id);
                setTenant(response);
            } catch (error) {
                setMessage('Kunde inte hämta organisationen', 'error', 6000);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTenant();
    }, [id]);

    if (isLoading) return <Loader />;
    if (!tenant)
        return (
            <Typography color="error">Organisationen hittades inte.</Typography>
        );

    const convertTypeOfAllowedTransactionRegistrations = (type) => {
        const types = {
            [AllowedTransactionRegistrations.defaultRegistration]: 'Default',
            [AllowedTransactionRegistrations.ambulance]: 'Ambulans',
            [AllowedTransactionRegistrations.vc]: 'VC',
        };
        return types[type] || 'Okänd';
    };

    const tenantData = [
        { label: 'OrganisationsID', value: tenant.id },
        { label: 'Admin Email', value: tenant.adminEmail },
        { label: 'Utfärdare', value: tenant.issuer },
        {
            label: 'Förvald inmatningsmetod',
            value: tenant.tenantConfig?.formRegistrationMethod,
        },
        {
            label: 'Typ av kund',
            value: convertTypeOfAllowedTransactionRegistrations(
                tenant.tenantConfig?.allowedTransactionRegistrations
            ),
        },
        { label: 'Status', value: tenant.isActive ? 'Aktiv' : 'Inaktiv' },
        {
            label: 'Licens t.o.m.',
            value: DateTime.fromISO(tenant.validUpto.toString()).toFormat(
                'yyyy-MM-dd'
            ),
        },
        { label: 'Anslutningssträng', value: tenant.connectionString },
    ];

    return (
        <div className="p-4 sm:p-6">
            {/* Back Button */}
            <div className="mb-4">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('..')}
                    color="primary"
                >
                    Tillbaka
                </Button>
            </div>

            {/* Header and Edit Button */}
            <div className="max-w-screen-md mx-auto mb-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <Typography
                        variant="h4"
                        className="text-xl sm:text-3xl font-bold"
                    >
                        {tenant.name}
                    </Typography>
                    <Button
                        startIcon={<EditNoteIcon />}
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            navigate(
                                `${NavigationRoutes.updateTenant}/${tenant.id}`
                            )
                        }
                    >
                        Redigera
                    </Button>
                </div>

                {/* Responsive Table */}
                <div className="overflow-x-auto mt-4 border rounded-md">
                    <TableContainer component={Paper} elevation={0}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow className="bg-gray-100">
                                    <TableCell className="font-bold">
                                        Fält
                                    </TableCell>
                                    <TableCell className="font-bold">
                                        Värde
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tenantData.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        className="odd:bg-gray-50"
                                    >
                                        <TableCell className="whitespace-nowrap">
                                            {row.label}
                                        </TableCell>
                                        <TableCell>
                                            {row.value || 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </div>
    );
};

export default TenantView;
